import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useBreadcrumbs, useUser } from '../hooks';
import { Header, Table, Groupper, Label, Pagination, Toolbar, Button, Input, Message, Toast, Icon } from 'react-frontier';
import { Modal, Dropdown } from 'semantic-ui-react';
import { UserClass, UserAssistance, EmploymentScheduleType, SetLoading, UserBankAccount, BankAccount } from '../SiteClasses';
import { getCheckInStatus, getStatusColor, getStatusLabel, bindClick, bindFormChange } from '../SiteUtil';
import { BankAccountModal } from '../components';
import UserAccess from '../UserAccess';
import Validator from '../Validator';
import moment from 'moment';
import API from '../API';

const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const PAGE_SIZE = 10;

var EmploymentUser = ()=>{
	var params = useParams<{ id: string }>();
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var [loadError, setLoadError] = useState<string>(null);
	var [userData, setUserData] = useState<UserClass>(null);
	var [logs, setLogs] = useState<UserAssistance[]>(null);
	var [page, setPage] = useState<number>(0);
	var [editModal, setEditModal] = useState<boolean>(false);
	var [editForm, setEditForm] = useState<{ first_name: string, last_name: string, payout_type: number }>({ first_name: '', last_name: '', payout_type: null });
	var [payoutTypes, setPayoutTypes] = useState<{ type_id: number, type_name: string }[]>([]);
	var [modalPrompts, setModalPrompts] = useState<string[]>([]);
	var [accounts, setAccounts] = useState<UserBankAccount[]>(null);
	var [addBankModal, setAddBankModal] = useState<boolean>(false);
	var [confirmAccount, setConfirmAccount] = useState<UserBankAccount>(null);
	var [editAccount, setEditAccount] = useState<UserBankAccount>(null);
	var [accountName, setAccountName] = useState<string>('');
	var [accountPrompts, setAccountPrompts] = useState<string[]>([]);

	useEffect(()=>{
		setTitle('Usuario #' + parseInt(params.id));
		var user_id = parseInt(params.id);
		API.getUser(user_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setUserData(res.data);
		}).catch(()=> setLoadError('Hubo un error cargando el usuario (LCL-1)'));
		API.getUserAssistance(user_id, 0, moment().add(1, 'year').endOf('day').unix()).then(res=>{
			if(res.error) return setLoadError(res.message);
			setLogs(res.data);
		}).catch(()=> setLoadError('Hubo un error cargando los registros (LCL-2)'));
		API.getPayoutTypes().then(res=>{
			if(!res.error) setPayoutTypes(res.data);
		}).catch(()=>{});
		API.getUserBankAccounts(user_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			setAccounts(res.data);
		}).catch(()=> Toast.error('Hubo un error cargando las cuentas bancarias (LCL-3)'));
	}, []);

	var onCreateAccount = (account: BankAccount)=>{
		setAddBankModal(false);
		Toast.success('Cuenta bancaria agregada');
		setAccounts([...(accounts || []), account]);
	};

	var setPrimaryAccount = (setLoading: SetLoading)=>{
		var account_id = confirmAccount.account_id;
		setLoading(true);
		API.setPrimaryBankAccount(account_id, userData.user_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Cuenta principal actualizada');
			setAccounts(prev => prev.map(a => ({ ...a, is_primary: a.account_id===account_id })));
			setConfirmAccount(null);
		}).catch(()=>{
			Toast.error('Hubo un error actualizando la cuenta principal (EU-PRI-1)');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var openEditAccount = (account: UserBankAccount)=>{
		setAccountName(account.name_account || '');
		setAccountPrompts([]);
		setEditAccount(account);
	};

	var saveAccount = (setLoading: SetLoading)=>{
		var name_account = accountName.trim();
		var { valid, prompts } = Validator({ name_account }, {
			name_account: [{ rule: 'minLength', params: [3], label: 'Titular' }],
		});
		setAccountPrompts(prompts);
		if(!valid) return;

		var account_id = editAccount.account_id;
		setLoading(true);
		API.editBankAccount(account_id, name_account, userData.user_id).then(res=>{
			if(res.error) return setAccountPrompts([res.message]);
			Toast.success('Cuenta bancaria actualizada');
			setAccounts(prev => prev.map(a => a.account_id===account_id ? { ...a, name_account } : a));
			setEditAccount(null);
		}).catch(()=>{
			setAccountPrompts(['Hubo un error actualizando la cuenta bancaria (EU-BKEDT-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var openEditModal = ()=>{
		setEditForm({ first_name: userData.first_name, last_name: userData.last_name, payout_type: userData.payout_type || null });
		setModalPrompts([]);
		setEditModal(true);
	};

	var saveEdit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(editForm, {
			first_name: [{ rule: 'minLength', params: [1], label: 'Nombre' }],
			last_name: [{ rule: 'minLength', params: [1], label: 'Apellido' }],
		});
		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.editUser(userData.user_id, editForm.first_name, editForm.last_name, editForm.payout_type).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Empleado actualizado');
			var payout_type_name = payoutTypes.find(t => t.type_id === editForm.payout_type)?.type_name;
			setUserData({ ...userData, first_name: editForm.first_name, last_name: editForm.last_name, payout_type: editForm.payout_type, payout_type_name });
			setEditModal(false);
		}).catch(()=>{
			setModalPrompts(['Hubo un error actualizando el empleado (U-EDT-1)']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var downloadPayments = ()=>{
		API.downloadPaymentsReport(`${userData.first_name} ${userData.last_name}`, userData.user_id).then(res=>{
			if(res.error) return Toast.error(res.message);
		}).catch(()=>{
			Toast.error('Hubo un error descargando los pagos (EU-PAY-1)');
		});
	};

	var onFormChange = bindFormChange(editForm, setEditForm);

	if(loadError) return <Header text='Error' subtext={loadError} />
	if(!userData || !logs) return <Header text='Cargando datos' loading/>

	var shown_logs = logs.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
	var hasNext = (page + 1) * PAGE_SIZE < logs.length;

	return <div className='ar user-screen'>
		<Groupper title={'Información de usuario'} width={500} style={{ marginTop: 20 }} fitted>
			{(hasAccess(UserAccess.USERS.EDIT_USERS) || hasAccess(UserAccess.EMPLOYMENTS.VIEW_PAYMENTS)) && (
				<Toolbar style={{ borderRadius: 0, boxShadow: 'none', borderLeft: '0', borderTop: '0', borderRight: '0' }}>
					<Toolbar.Item iconName='edit' text='Editar' onClick={openEditModal} if={hasAccess(UserAccess.USERS.EDIT_USERS)} />
					<Toolbar.Item iconName='money-bill' text='Pagos' onClick={downloadPayments} if={hasAccess(UserAccess.EMPLOYMENTS.VIEW_PAYMENTS)} />
				</Toolbar>
			)}
			<Table details striped>
				<Table.Row data={['ID', userData.user_id]}/>
				<Table.Row data={['Nombre', (userData.first_name + ' ' + userData.last_name)]}/>
				<Table.Row data={['Correo electrónico', userData.email]}/>
				<Table.Row data={['RFC', userData.account_rfc || <Icon name='minus' />]}/>
				<Table.Row data={['Código postal', userData.account_postal_code || <Icon name='minus' />]}/>
				<Table.Row data={['Tipo de pago', userData.payout_type_name || '-']}/>
				<Table.Row data={['Creado', moment.unix(userData.date_created).format('DD/MM/YY HH:mm:ss')]}/>
			</Table>
		</Groupper>
		<Table
			title='Cuentas bancarias'
			striped
			headers={['ID', 'Tipo', 'Titular', 'Banco', 'Cuenta', 'Principal', <Icon name='wrench' />]}
			centeredIndexes={[5, 6]}
			collapsingIndexes={[5, 6]}
			style={{ width: 900, maxWidth: '100%', margin: 'auto', marginTop: 15 }}
			button={hasAccess(UserAccess.USERS.EDIT_USERS) && (
				<Button text='Agregar' color='black' iconName='plus-circle' size='small' onClick={bindClick(setAddBankModal, true)} />
			)}
		>
			{accounts && accounts.length!==0 ? accounts.map(a=>(
				<Table.Row key={a.account_id} centeredIndexes={[5, 6]} data={[
					a.account_id,
					<><Icon name={a.clabe ? 'landmark' : 'credit-card'} style={{ marginRight: 5 }} />{a.clabe ? 'SPEI' : 'Tarjeta'}</>,
					a.name_account,
					a.bank,
					a.clabe ? `**${a.clabe.slice(-5)}` : `**${(a.card_number || '').slice(-4)}`,
					a.is_primary ? (
						<Icon name='circle-check' color='green' />
					) : hasAccess(UserAccess.USERS.EDIT_USERS) ? (
						<Button size='tiny' text='Seleccionar' onClick={bindClick(setConfirmAccount, a)} />
					) : '-',
					hasAccess(UserAccess.USERS.EDIT_USERS) ? (
						<Dropdown icon={(
							<div className="fr label" style={{ padding: '3px 10px' }}>
								<Icon name='ellipsis' style={{ margin: 0 }} />
							</div>
						)} className='icon'>
							<Dropdown.Menu>
								<Dropdown.Item onClick={bindClick(openEditAccount, a)}>
									<Icon name="pen" style={{ marginRight: 3 }} /> Editar
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					) : '-',
				]} />
			)) : (
				<Table.Cell row className='empty' colSpan={7}>
					No hay cuentas bancarias registradas
				</Table.Cell>
			)}
		</Table>
		<Table
			title='Registros'
			headers={['Empleo', 'Día', 'Fecha', 'Horario', 'Entrada', 'Salida', 'Estado']}
			centeredIndexes={[4, 5, 6]}
			collapsingIndexes={[6]}
			style={{ width: 800, margin: 'auto', marginTop: 15 }}
			striped
			footer={(hasNext || page !== 0) && (
				<Table.Cell colSpan={7} row>
					<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
						<Pagination page={page} onPageChange={setPage} hasNext={hasNext} />
					</div>
				</Table.Cell>
			)}
		>
			{shown_logs.length !== 0 ? (
				shown_logs.map((log, idx)=>{
					var status = getCheckInStatus(log);
					var isRest = log.log_type_id || log.type_id === EmploymentScheduleType.REST;
					return <Table.Row
						key={`${log.log_id}-${idx}`}
						centeredIndexes={[4, 5, 6]}
						data={[
							log.employment_name,
							DAYS[moment.unix(log.date_start).day()],
							moment.unix(log.date_start).format('DD/MM/YY'),
							(log.time_start !== null && log.time_end !== null)
								? `${String(Math.floor(log.time_start / 60)).padStart(2, '0')}:${String(log.time_start % 60).padStart(2, '0')} - ${String(Math.floor(log.time_end / 60)).padStart(2, '0')}:${String(log.time_end % 60).padStart(2, '0')}`
								: 'N/A',
							isRest ? '-' : (log.date_start ? moment.unix(log.date_start).format('HH:mm') : 'N/A'),
							isRest ? '-' : (log.date_end && log.date_end > 0 ? moment.unix(log.date_end).format('HH:mm') : '-'),
							<Label color={getStatusColor(status)}>{getStatusLabel(status)}</Label>
						]}
					/>
				})
			) : (
				<Table.Cell row className='empty' colSpan={7}>
					No se encontraron registros
				</Table.Cell>
			)}
		</Table>
		<BankAccountModal
			open={addBankModal}
			user_id={userData.user_id}
			onClose={bindClick(setAddBankModal, false)}
			onCreate={onCreateAccount}
		/>
		<Modal open={!!confirmAccount} onClose={bindClick(setConfirmAccount, null)} size='mini'>
			<Modal.Header>Cuenta principal</Modal.Header>
			<Modal.Content>
				<Header text='¿Establecer como principal?' subtext={confirmAccount ? `${confirmAccount.bank} ${confirmAccount.clabe ? `**${confirmAccount.clabe.slice(-5)}` : `**${(confirmAccount.card_number || '').slice(-4)}`}` : ''} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setConfirmAccount, null)} />
				<Button text='Confirmar' color='black' onClick={setPrimaryAccount} />
			</Modal.Actions>
		</Modal>
		<Modal open={!!editAccount} onClose={bindClick(setEditAccount, null)} size='mini'>
			<Modal.Header>Editar cuenta bancaria</Modal.Header>
			{editAccount && <Modal.Content>
				<Input label='Titular' value={accountName} onChange={setAccountName} />
				<Input label='Tipo' readonly value={editAccount.clabe ? 'SPEI (Transferencia)' : 'Tarjeta bancaria'} style={{ marginTop: 10 }} />
				<Input label='Banco' readonly value={editAccount.bank || ''} style={{ marginTop: 10 }} />
				<Input label={editAccount.clabe ? 'CLABE' : 'Tarjeta'} readonly value={editAccount.clabe || editAccount.card_number || ''} style={{ marginTop: 10 }} />
				<Message type='error' list={accountPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setEditAccount, null)} />
				<Button text='Guardar' color='black' onClick={saveAccount} />
			</Modal.Actions>
		</Modal>
		<Modal open={editModal} onClose={bindClick(setEditModal, false)} size='mini'>
			<Modal.Header>Editar empleado</Modal.Header>
			<Modal.Content>
				<Input label='Nombre' value={editForm.first_name} onChange={onFormChange('first_name')} />
				<Input label='Apellido' value={editForm.last_name} onChange={onFormChange('last_name')} style={{ marginTop: 10 }} />
				<div className='field' style={{ marginTop: 10 }}>
					<label style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>Tipo de pago</label>
					<Dropdown
						selection fluid clearable placeholder='Selecciona un tipo de pago'
						value={editForm.payout_type}
						options={payoutTypes.map(t => ({ key: t.type_id, value: t.type_id, text: t.type_name }))}
						onChange={(_, { value })=> setEditForm({ ...editForm, payout_type: value as number })}
					/>
				</div>
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cancelar' basic onClick={bindClick(setEditModal, false)} />
				<Button text='Guardar' color='black' onClick={saveEdit} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default EmploymentUser;
